/**
 * Notification API Contract
 *
 * Mirrors: backend/src/routes/notification.routes.ts
 * 1:1 parity with existing Express routes
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { GetNotificationsQuerySchema, MarkNotificationsReadInputSchema, UpdateNotificationPreferenceInputSchema, RegisterDeviceInputSchema, NotificationType, NotificationChannel, NotificationResponseSchema, UserSessionResponseSchema, } from "@hourly/api/notification";
import { uuidSchema } from "@hourly/api/validation-common";
// ============================================
// Response Schemas
// ============================================
const NotificationListResponseSchema = z.object({
    notifications: z.array(NotificationResponseSchema),
    nextCursor: z.string().uuid().optional(),
    hasMore: z.boolean(),
    unreadCount: z.number(),
});
const NotificationPreferenceResponseSchema = z.object({
    type: z.nativeEnum(NotificationType),
    enabled: z.boolean(),
    channels: z.array(z.nativeEnum(NotificationChannel)),
});
const NotificationPreferencesResponseSchema = z.object({
    preferences: z.array(NotificationPreferenceResponseSchema),
});
const NotificationUnreadCountResponseSchema = z.object({
    count: z.number(),
});
const MarkReadResponseSchema = z.object({
    count: z.number(),
});
const LogoutAllDevicesResponseSchema = z.object({
    count: z.number(),
});
const DeviceSessionsResponseSchema = z.object({
    sessions: z.array(UserSessionResponseSchema),
});
// Helper for success responses
const SuccessResponseSchema = (dataSchema) => z.object({ success: z.literal(true), data: dataSchema });
const SuccessOnlyResponseSchema = z.object({ success: z.literal(true) });
// ============================================
// Notification Contracts
// ============================================
/**
 * GET /api/notifications
 * List user's notifications with pagination
 */
export const getNotifications = oc
    .route({
    method: "GET",
    path: "/notifications",
    summary: "List user's notifications with pagination",
    tags: ["Notifications"],
})
    .input(GetNotificationsQuerySchema)
    .output(SuccessResponseSchema(NotificationListResponseSchema));
/**
 * GET /api/notifications/unread-count
 * Get total unread notification count for user
 */
export const getNotificationUnreadCount = oc
    .route({
    method: "GET",
    path: "/notifications/unread-count",
    summary: "Get total unread notification count",
    tags: ["Notifications"],
})
    .input(z.object({}))
    .output(SuccessResponseSchema(NotificationUnreadCountResponseSchema));
/**
 * POST /api/notifications/mark-read
 * Mark notifications as read (all or specific IDs)
 */
export const markNotificationsRead = oc
    .route({
    method: "POST",
    path: "/notifications/mark-read",
    summary: "Mark notifications as read",
    tags: ["Notifications"],
})
    .input(MarkNotificationsReadInputSchema)
    .output(SuccessResponseSchema(MarkReadResponseSchema));
/**
 * POST /api/notifications/mark-all-read
 * Mark all notifications as read for the authenticated user
 */
export const markAllNotificationsRead = oc
    .route({
    method: "POST",
    path: "/notifications/mark-all-read",
    summary: "Mark all notifications as read",
    tags: ["Notifications"],
})
    .input(z.object({}))
    .output(SuccessOnlyResponseSchema);
// ============================================
// Notification Preferences Contracts
// ============================================
/**
 * GET /api/notifications/preferences
 * Get user's notification preferences
 */
export const getNotificationPreferences = oc
    .route({
    method: "GET",
    path: "/notifications/preferences",
    summary: "Get notification preferences",
    tags: ["Notifications"],
})
    .input(z.object({}))
    .output(SuccessResponseSchema(NotificationPreferencesResponseSchema));
/**
 * PUT /api/notifications/preferences
 * Update a notification preference (returns all preferences after update)
 */
export const updateNotificationPreference = oc
    .route({
    method: "PUT",
    path: "/notifications/preferences",
    summary: "Update notification preference",
    tags: ["Notifications"],
})
    .input(UpdateNotificationPreferenceInputSchema)
    .output(SuccessResponseSchema(NotificationPreferencesResponseSchema));
// ============================================
// Device Session Contracts
// ============================================
/**
 * POST /api/notifications/devices
 * Register a device for push notifications
 */
export const registerDevice = oc
    .route({
    method: "POST",
    path: "/notifications/devices",
    summary: "Register a device for push notifications",
    tags: ["Notifications"],
    successStatus: 201,
})
    .input(RegisterDeviceInputSchema)
    .output(SuccessResponseSchema(UserSessionResponseSchema));
/**
 * GET /api/notifications/devices
 * Get user's active device sessions
 */
export const getActiveSessions = oc
    .route({
    method: "GET",
    path: "/notifications/devices",
    summary: "Get active device sessions",
    tags: ["Notifications"],
})
    .input(z.object({}))
    .output(SuccessResponseSchema(DeviceSessionsResponseSchema));
/**
 * DELETE /api/notifications/devices/:id
 * Deactivate a specific device session
 */
export const deactivateSession = oc
    .route({
    method: "DELETE",
    path: "/notifications/devices/{id}",
    summary: "Deactivate a device session",
    tags: ["Notifications"],
})
    .input(z.object({ id: uuidSchema }))
    .output(SuccessOnlyResponseSchema);
/**
 * POST /api/notifications/devices/logout-all
 * Deactivate all device sessions for the user
 */
export const logoutAllDevices = oc
    .route({
    method: "POST",
    path: "/notifications/devices/logout-all",
    summary: "Logout all devices",
    tags: ["Notifications"],
})
    .input(z.object({}))
    .output(SuccessResponseSchema(LogoutAllDevicesResponseSchema));
// ============================================
// Contract Router
// ============================================
export const notificationContract = {
    // Notification operations
    getNotifications,
    getUnreadCount: getNotificationUnreadCount,
    markNotificationsRead,
    markAllNotificationsRead,
    // Preference operations
    getNotificationPreferences,
    updateNotificationPreference,
    // Device session operations
    registerDevice,
    getActiveSessions,
    deactivateSession,
    logoutAllDevices,
};
//# sourceMappingURL=notification.js.map